import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import HeaderWithSearch from "./HeaderWithSearch";
import "./ChoreVault.css";

/* ======================== CONSTANTS & HELPERS ======================== */
const DB_KEY = "choreVaultDB";

function getDB() {
  const raw = localStorage.getItem(DB_KEY);
  if (!raw) {
    const defaultDB = { users: [], chores: [], notifications: [], activities: [] };
    localStorage.setItem(DB_KEY, JSON.stringify(defaultDB));
    return defaultDB;
  }
  return JSON.parse(raw);
}

function saveDB(db) {
  localStorage.setItem(DB_KEY, JSON.stringify(db));
}

function generateId() {
  return "id_" + Date.now().toString(36) + "_" + Math.random().toString(36).substr(2, 9);
}

function generateFamilyCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
  return code;
}

function getCategoryEmoji(category) {
  const emojis = { cleaning: "🧹", kitchen: "🍽️", outdoor: "🌿", laundry: "👕", pets: "🐾", homework: "📚", other: "⭐" };
  return emojis[category] || "⭐";
}

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(dateStr);
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getChartOptions(prefix = "") {
  return {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(15, 15, 35, 0.95)",
        titleColor: "#e8eaed",
        bodyColor: "#9ca3af",
        borderColor: "rgba(0, 240, 255, 0.3)",
        borderWidth: 1,
        cornerRadius: 10,
        titleFont: { family: "Inter" },
        bodyFont: { family: "JetBrains Mono" },
        callbacks: {
          label: function (context) {
            return `${prefix}${context.parsed.y?.toFixed(2) || context.parsed}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: "#6b7280", font: { family: "Inter", size: 11 } },
        grid: { color: "rgba(255,255,255,0.03)" },
        border: { color: "rgba(255,255,255,0.06)" },
      },
      y: {
        ticks: {
          color: "#6b7280",
          font: { family: "JetBrains Mono", size: 11 },
          callback: function (value) { return prefix + value; },
        },
        grid: { color: "rgba(255,255,255,0.03)" },
        border: { color: "rgba(255,255,255,0.06)" },
        beginAtZero: true,
      },
    },
  };
}

/* ======================== MAIN COMPONENT ======================== */
export default function ChoreVault() {
  /* ---------- State ---------- */
  const [currentUser, setCurrentUser] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [selectedRole, setSelectedRole] = useState("parent");
  const [parentTab, setParentTab] = useState("overview");
  const [childTab, setChildTab] = useState("childOverview");
  const [choreFilter, setChoreFilter] = useState("all");
  const [childChoreFilter, setChildChoreFilter] = useState("all");
  const [showAddChoreModal, setShowAddChoreModal] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [dbVersion, setDbVersion] = useState(0);
  const [chartReady, setChartReady] = useState(false);

  // Form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [familyCode, setFamilyCode] = useState("");
  const [choreName, setChoreName] = useState("");
  const [choreDesc, setChoreDesc] = useState("");
  const [choreAmount, setChoreAmount] = useState("");
  const [choreCategory, setChoreCategory] = useState("cleaning");
  const [choreAssignee, setChoreAssignee] = useState("all");
  const [choreRecurrence, setChoreRecurrence] = useState("once");

  /* ---------- Refs ---------- */
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const chartInstances = useRef({});
  const chartCanvasRefs = useRef({});

  /* ---------- CDN Loading ---------- */
  useEffect(() => {
    // Load Google Fonts
    if (!document.querySelector('link[href*="Orbitron"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap";
      document.head.appendChild(link);
    }

    // Load Font Awesome
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const fa = document.createElement("link");
      fa.rel = "stylesheet";
      fa.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css";
      document.head.appendChild(fa);
    }

    // Load Chart.js
    if (window.Chart) {
      setChartReady(true);
    } else if (!document.querySelector('script[src*="chart.js"]')) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js";
      script.onload = () => setChartReady(true);
      document.head.appendChild(script);
    } else {
      const check = setInterval(() => {
        if (window.Chart) {
          setChartReady(true);
          clearInterval(check);
        }
      }, 200);
      return () => clearInterval(check);
    }
  }, []);

  /* ---------- Auto Login ---------- */
  useEffect(() => {
    const saved = localStorage.getItem("choreVault_currentUser");
    if (saved) {
      const user = JSON.parse(saved);
      const db = getDB();
      const found = db.users.find((u) => u.id === user.id);
      if (found) setCurrentUser(found);
    }
  }, []);

  /* ---------- Particle System ---------- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let particles = [];
    const PARTICLE_COUNT = 60;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.opacity = Math.random() * 0.5 + 0.1;
        const colors = ["0, 240, 255", "168, 85, 247", "255, 45, 149", "0, 255, 136"];
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
      }
    }

    for (let i = 0; i < PARTICLE_COUNT; i++) particles.push(new Particle());

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => { p.update(); p.draw(); });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 240, 255, ${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animFrameRef.current = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      window.removeEventListener("resize", resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  /* ---------- Chart Rendering ---------- */
  const destroyChart = useCallback((id) => {
    if (chartInstances.current[id]) {
      chartInstances.current[id].destroy();
      delete chartInstances.current[id];
    }
  }, []);

  useEffect(() => {
    if (!chartReady || !currentUser || !window.Chart) return;

    const db = getDB();
    const Chart = window.Chart;

    if (currentUser.role === "parent") {
      // Monthly Spending Chart
      const msCanvas = chartCanvasRefs.current.monthlySpending;
      if (msCanvas) {
        const approved = db.chores.filter((c) => c.familyCode === currentUser.familyCode && c.status === "approved");
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const now = new Date();
        const monthlyData = {};
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          monthlyData[`${monthNames[d.getMonth()]} ${d.getFullYear()}`] = 0;
        }
        approved.forEach((c) => {
          const d = new Date(c.approvedAt || c.completedAt);
          const key = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
          if (monthlyData.hasOwnProperty(key)) monthlyData[key] += c.amount;
        });

        destroyChart("monthlySpending");
        chartInstances.current.monthlySpending = new Chart(msCanvas.getContext("2d"), {
          type: "bar",
          data: {
            labels: Object.keys(monthlyData),
            datasets: [{
              label: "Spending ($)", data: Object.values(monthlyData),
              backgroundColor: "rgba(0, 240, 255, 0.3)", borderColor: "rgba(0, 240, 255, 0.8)",
              borderWidth: 2, borderRadius: 8, borderSkipped: false,
            }],
          },
          options: getChartOptions("$"),
        });
      }

      // Chore Breakdown Chart
      const cbCanvas = chartCanvasRefs.current.choreBreakdown;
      if (cbCanvas) {
        const chores = db.chores.filter((c) => c.familyCode === currentUser.familyCode);
        const categories = {};
        chores.forEach((c) => { categories[c.category] = (categories[c.category] || 0) + 1; });
        const colors = ["rgba(0,240,255,0.7)", "rgba(255,45,149,0.7)", "rgba(0,255,136,0.7)", "rgba(168,85,247,0.7)", "rgba(251,191,36,0.7)", "rgba(99,102,241,0.7)", "rgba(244,63,94,0.7)"];

        destroyChart("choreBreakdown");
        chartInstances.current.choreBreakdown = new Chart(cbCanvas.getContext("2d"), {
          type: "doughnut",
          data: {
            labels: Object.keys(categories).map((c) => getCategoryEmoji(c) + " " + c),
            datasets: [{ data: Object.values(categories), backgroundColor: colors.slice(0, Object.keys(categories).length), borderWidth: 0, hoverOffset: 10 }],
          },
          options: {
            responsive: true, maintainAspectRatio: true,
            plugins: { legend: { position: "bottom", labels: { color: "#9ca3af", padding: 15, font: { family: "Inter" } } } },
            cutout: "65%",
          },
        });
      }

      // Analytics: Spending Over Time
      const sotCanvas = chartCanvasRefs.current.spendingOverTime;
      if (sotCanvas) {
        const approved = db.chores.filter((c) => c.familyCode === currentUser.familyCode && c.status === "approved");
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const now = new Date();
        const timeData = {};
        for (let i = 11; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          timeData[`${monthNames[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`] = 0;
        }
        approved.forEach((c) => {
          const d = new Date(c.approvedAt || c.completedAt);
          const key = `${monthNames[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
          if (timeData.hasOwnProperty(key)) timeData[key] += c.amount;
        });

        destroyChart("spendingOverTime");
        chartInstances.current.spendingOverTime = new Chart(sotCanvas.getContext("2d"), {
          type: "line",
          data: {
            labels: Object.keys(timeData),
            datasets: [{
              label: "Spending ($)", data: Object.values(timeData),
              borderColor: "rgba(0,240,255,0.8)", backgroundColor: "rgba(0,240,255,0.1)",
              fill: true, tension: 0.4, pointBackgroundColor: "rgba(0,240,255,1)",
              pointBorderColor: "rgba(0,240,255,1)", pointRadius: 4, pointHoverRadius: 6, borderWidth: 2,
            }],
          },
          options: getChartOptions("$"),
        });
      }

      // Analytics: Per-Child Spending
      const pcCanvas = chartCanvasRefs.current.perChild;
      if (pcCanvas) {
        const approved = db.chores.filter((c) => c.familyCode === currentUser.familyCode && c.status === "approved");
        const children = db.users.filter((u) => u.familyCode === currentUser.familyCode && u.role === "child");
        const childSpending = {};
        children.forEach((child) => {
          childSpending[child.name] = approved.filter((c) => c.completedBy === child.id).reduce((sum, c) => sum + c.amount, 0);
        });
        const childColors = ["rgba(0,240,255,0.7)", "rgba(255,45,149,0.7)", "rgba(0,255,136,0.7)", "rgba(168,85,247,0.7)", "rgba(251,191,36,0.7)"];

        destroyChart("perChild");
        chartInstances.current.perChild = new Chart(pcCanvas.getContext("2d"), {
          type: "bar",
          data: {
            labels: Object.keys(childSpending),
            datasets: [{ label: "Total Earned ($)", data: Object.values(childSpending), backgroundColor: childColors.slice(0, children.length), borderWidth: 0, borderRadius: 8, borderSkipped: false }],
          },
          options: getChartOptions("$"),
        });
      }

      // Analytics: Top Categories
      const tcCanvas = chartCanvasRefs.current.topCategories;
      if (tcCanvas) {
        const approved = db.chores.filter((c) => c.familyCode === currentUser.familyCode && c.status === "approved");
        const catSpending = {};
        approved.forEach((c) => { catSpending[c.category] = (catSpending[c.category] || 0) + c.amount; });

        destroyChart("topCategories");
        chartInstances.current.topCategories = new Chart(tcCanvas.getContext("2d"), {
          type: "polarArea",
          data: {
            labels: Object.keys(catSpending).map((c) => getCategoryEmoji(c) + " " + c),
            datasets: [{
              data: Object.values(catSpending),
              backgroundColor: ["rgba(0,240,255,0.5)", "rgba(255,45,149,0.5)", "rgba(0,255,136,0.5)", "rgba(168,85,247,0.5)", "rgba(251,191,36,0.5)", "rgba(99,102,241,0.5)", "rgba(244,63,94,0.5)"],
              borderWidth: 1, borderColor: "rgba(255,255,255,0.1)",
            }],
          },
          options: {
            responsive: true, maintainAspectRatio: true,
            plugins: { legend: { position: "bottom", labels: { color: "#9ca3af", font: { family: "Inter" } } } },
            scales: { r: { ticks: { color: "#6b7280", backdropColor: "transparent" }, grid: { color: "rgba(255,255,255,0.05)" } } },
          },
        });
      }
    }

    if (currentUser.role === "child") {
      // Child Monthly Chart
      const cmCanvas = chartCanvasRefs.current.childMonthly;
      if (cmCanvas) {
        const approved = db.chores.filter((c) => c.familyCode === currentUser.familyCode && c.completedBy === currentUser.id && c.status === "approved");
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const now = new Date();
        const monthlyData = {};
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          monthlyData[monthNames[d.getMonth()]] = 0;
        }
        approved.forEach((c) => {
          const d = new Date(c.approvedAt);
          const key = monthNames[d.getMonth()];
          if (monthlyData.hasOwnProperty(key)) monthlyData[key] += c.amount;
        });

        destroyChart("childMonthly");
        chartInstances.current.childMonthly = new Chart(cmCanvas.getContext("2d"), {
          type: "bar",
          data: {
            labels: Object.keys(monthlyData),
            datasets: [{
              label: "Earned ($)", data: Object.values(monthlyData),
              backgroundColor: ["rgba(0,240,255,0.4)", "rgba(168,85,247,0.4)", "rgba(255,45,149,0.4)", "rgba(0,255,136,0.4)", "rgba(251,191,36,0.4)", "rgba(99,102,241,0.4)"],
              borderColor: ["rgba(0,240,255,0.8)", "rgba(168,85,247,0.8)", "rgba(255,45,149,0.8)", "rgba(0,255,136,0.8)", "rgba(251,191,36,0.8)", "rgba(99,102,241,0.8)"],
              borderWidth: 2, borderRadius: 8, borderSkipped: false,
            }],
          },
          options: getChartOptions("$"),
        });
      }

      // Child Earnings Over Time
      const ceCanvas = chartCanvasRefs.current.childEarnings;
      if (ceCanvas) {
        const approved = db.chores.filter((c) => c.familyCode === currentUser.familyCode && c.completedBy === currentUser.id && c.status === "approved");
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const now = new Date();
        const timeData = {};
        for (let i = 11; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          timeData[`${monthNames[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`] = 0;
        }
        approved.forEach((c) => {
          const d = new Date(c.approvedAt);
          const key = `${monthNames[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
          if (timeData.hasOwnProperty(key)) timeData[key] += c.amount;
        });

        destroyChart("childEarnings");
        chartInstances.current.childEarnings = new Chart(ceCanvas.getContext("2d"), {
          type: "line",
          data: {
            labels: Object.keys(timeData),
            datasets: [{
              label: "Earnings ($)", data: Object.values(timeData),
              borderColor: "rgba(0,255,136,0.8)", backgroundColor: "rgba(0,255,136,0.1)",
              fill: true, tension: 0.4, pointBackgroundColor: "rgba(0,255,136,1)",
              pointBorderColor: "rgba(0,255,136,1)", pointRadius: 4, pointHoverRadius: 6, borderWidth: 2,
            }],
          },
          options: getChartOptions("$"),
        });
      }
    }

    const chartInstancesSnapshot = chartInstances.current;
    return () => {
      Object.keys(chartInstancesSnapshot).forEach((k) => {
        if (chartInstancesSnapshot[k]) {
          chartInstancesSnapshot[k].destroy();
          delete chartInstancesSnapshot[k];
        }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartReady, currentUser, dbVersion, parentTab, childTab]);

  /* ---------- Periodic Refresh ---------- */
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(() => {
      setDbVersion((v) => v + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  /* ---------- Keyboard Shortcuts ---------- */
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        setShowAddChoreModal(false);
        setShowUserMenu(false);
        setShowNotifPanel(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  /* ---------- Click Outside ---------- */
  useEffect(() => {
    function handleClick(e) {
      if (!e.target.closest(".cv-user-avatar") && !e.target.closest(".cv-user-menu")) {
        setShowUserMenu(false);
      }
      if (!e.target.closest(".cv-notification-bell") && !e.target.closest(".cv-notif-panel")) {
        setShowNotifPanel(false);
      }
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  /* ---------- Toast ---------- */
  const showToast = useCallback((message, type = "info") => {
    const id = generateId();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 300);
    }, 4000);
  }, []);

  /* ---------- Auth Handlers ---------- */
  function handleLogin() {
    if (!loginEmail || !loginPassword) { showToast("Please fill in all fields", "error"); return; }
    const db = getDB();
    const user = db.users.find((u) => u.email === loginEmail.trim().toLowerCase() && u.password === loginPassword);
    if (!user) { showToast("Invalid email or password", "error"); return; }
    showToast(`Welcome back, ${user.name}!`, "success");
    loginUser(user);
  }

  function handleSignup() {
    const name = signupName.trim();
    const email = signupEmail.trim().toLowerCase();
    const password = signupPassword;
    const fc = familyCode.trim().toUpperCase();

    if (!name || !email || !password) { showToast("Please fill in all fields", "error"); return; }
    if (password.length < 6) { showToast("Password must be at least 6 characters", "error"); return; }

    const db = getDB();
    if (db.users.find((u) => u.email === email)) { showToast("Email already registered", "error"); return; }

    let userFamilyCode = fc || generateFamilyCode();
    if (selectedRole === "child" && fc) {
      const parentExists = db.users.find((u) => u.familyCode === fc && u.role === "parent");
      if (!parentExists) { showToast("Invalid family code. Ask your parent for the correct code.", "error"); return; }
    }

    const user = {
      id: generateId(), name, email, password, role: selectedRole,
      familyCode: userFamilyCode, createdAt: new Date().toISOString(),
    };
    db.users.push(user);
    saveDB(db);
    showToast(`Account created! Welcome to ChoreVault, ${name}!`, "success");
    loginUser(user);
  }

  function loginUser(user) {
    setCurrentUser(user);
    localStorage.setItem("choreVault_currentUser", JSON.stringify(user));
    setDbVersion((v) => v + 1);
  }

  function handleLogout() {
    setCurrentUser(null);
    localStorage.removeItem("choreVault_currentUser");
    setLoginEmail(""); setLoginPassword("");
    setSignupName(""); setSignupEmail(""); setSignupPassword(""); setFamilyCode("");
    setShowUserMenu(false); setShowNotifPanel(false);
    setAuthMode("login");
    setParentTab("overview"); setChildTab("childOverview");
    Object.keys(chartInstances.current).forEach((k) => destroyChart(k));
    showToast("Logged out successfully", "info");
  }

  /* ---------- Chore Actions ---------- */
  function addChore() {
    if (!choreName.trim()) { showToast("Please enter a chore name", "error"); return; }
    const amount = parseFloat(choreAmount);
    if (isNaN(amount) || amount <= 0) { showToast("Please enter a valid amount", "error"); return; }

    const db = getDB();
    const chore = {
      id: generateId(), name: choreName.trim(), description: choreDesc.trim(), amount,
      category: choreCategory, assigneeId: choreAssignee, recurrence: choreRecurrence,
      familyCode: currentUser.familyCode, createdBy: currentUser.id, status: "pending",
      createdAt: new Date().toISOString(), completedAt: null, completedBy: null, approvedAt: null,
    };
    db.chores.push(chore);
    db.activities.push({
      id: generateId(), familyCode: currentUser.familyCode, type: "added",
      message: `${currentUser.name} added chore: "${choreName.trim()}" ($${amount.toFixed(2)})`,
      timestamp: new Date().toISOString(), amount,
    });
    saveDB(db);
    setShowAddChoreModal(false);
    setChoreName(""); setChoreDesc(""); setChoreAmount(""); setChoreCategory("cleaning"); setChoreAssignee("all"); setChoreRecurrence("once");
    showToast(`Chore "${choreName.trim()}" added!`, "success");
    setDbVersion((v) => v + 1);
  }

  function approveChore(choreId) {
    const db = getDB();
    const chore = db.chores.find((c) => c.id === choreId);
    if (!chore) return;
    chore.status = "approved";
    chore.approvedAt = new Date().toISOString();
    db.activities.push({
      id: generateId(), familyCode: currentUser.familyCode, type: "approved",
      message: `${currentUser.name} approved "${chore.name}" — $${chore.amount.toFixed(2)} paid!`,
      timestamp: new Date().toISOString(), amount: chore.amount,
    });
    const completedByUser = db.users.find((u) => u.id === chore.completedBy);
    if (completedByUser) {
      db.notifications.push({
        id: generateId(), userId: completedByUser.id, familyCode: currentUser.familyCode,
        message: `💰 Your chore "${chore.name}" was approved! You earned $${chore.amount.toFixed(2)}!`,
        timestamp: new Date().toISOString(), read: false,
      });
    }
    saveDB(db);
    showToast(`Approved! $${chore.amount.toFixed(2)} paid for "${chore.name}"`, "success");
    setDbVersion((v) => v + 1);
  }

  function rejectChore(choreId) {
    const db = getDB();
    const chore = db.chores.find((c) => c.id === choreId);
    if (!chore) return;
    const completedBy = chore.completedBy;
    chore.status = "pending"; chore.completedAt = null; chore.completedBy = null;
    if (completedBy) {
      db.notifications.push({
        id: generateId(), userId: completedBy, familyCode: currentUser.familyCode,
        message: `🔄 Your chore "${chore.name}" was sent back. Please try again!`,
        timestamp: new Date().toISOString(), read: false,
      });
    }
    saveDB(db);
    showToast(`"${chore.name}" sent back for redo`, "info");
    setDbVersion((v) => v + 1);
  }

  function deleteChore(choreId) {
    const db = getDB();
    db.chores = db.chores.filter((c) => c.id !== choreId);
    saveDB(db);
    showToast("Chore deleted", "info");
    setDbVersion((v) => v + 1);
  }

  function markChoreDone(choreId) {
    const db = getDB();
    const chore = db.chores.find((c) => c.id === choreId);
    if (!chore) return;
    chore.status = "done";
    chore.completedAt = new Date().toISOString();
    chore.completedBy = currentUser.id;
    db.activities.push({
      id: generateId(), familyCode: currentUser.familyCode, type: "completed",
      message: `${currentUser.name} completed "${chore.name}"`,
      timestamp: new Date().toISOString(), amount: chore.amount,
    });
    const parents = db.users.filter((u) => u.familyCode === currentUser.familyCode && u.role === "parent");
    parents.forEach((parent) => {
      db.notifications.push({
        id: generateId(), userId: parent.id, familyCode: currentUser.familyCode,
        message: `🎉 ${currentUser.name} completed "${chore.name}" ($${chore.amount.toFixed(2)})! Review and approve it.`,
        timestamp: new Date().toISOString(), read: false,
      });
    });
    saveDB(db);
    showToast(`Great job! "${chore.name}" marked as done! 🎉`, "success");
    setDbVersion((v) => v + 1);
  }

  /* ---------- Notification Actions ---------- */
  function handleToggleNotifications(e) {
    e.stopPropagation();
    const wasHidden = !showNotifPanel;
    setShowUserMenu(false);
    setShowNotifPanel(!showNotifPanel);
    if (wasHidden) {
      const db = getDB();
      db.notifications.filter((n) => n.userId === currentUser.id).forEach((n) => (n.read = true));
      saveDB(db);
      setTimeout(() => setDbVersion((v) => v + 1), 500);
    }
  }

  function copyFamilyCode() {
    navigator.clipboard.writeText(currentUser.familyCode).then(() => {
      showToast("Family code copied to clipboard!", "success");
    }).catch(() => {
      showToast("Failed to copy. Code: " + currentUser.familyCode, "info");
    });
  }

  /* ---------- Computed Data ---------- */
  const db = getDB();

  // Parent stats
  const familyChores = currentUser ? db.chores.filter((c) => c.familyCode === currentUser.familyCode) : [];
  const approvedChores = familyChores.filter((c) => c.status === "approved");
  const totalPaid = approvedChores.reduce((sum, c) => sum + c.amount, 0);
  const now = new Date();
  const thisMonthApproved = approvedChores.filter((c) => {
    const d = new Date(c.completedAt || c.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthPaid = thisMonthApproved.reduce((sum, c) => sum + c.amount, 0);
  const completedCount = familyChores.filter((c) => c.status === "done" || c.status === "approved").length;
  const pendingCount = familyChores.filter((c) => c.status === "pending").length;

  // Child stats
  const myChores = currentUser ? db.chores.filter((c) =>
    c.familyCode === currentUser.familyCode && (c.assigneeId === currentUser.id || c.assigneeId === "all")
  ) : [];
  const myApproved = myChores.filter((c) => c.status === "approved" && c.completedBy === currentUser?.id);
  const childTotalEarned = myApproved.reduce((sum, c) => sum + c.amount, 0);
  const childMonthApproved = myApproved.filter((c) => {
    const d = new Date(c.completedAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const childMonthEarned = childMonthApproved.reduce((sum, c) => sum + c.amount, 0);
  const childCompletedCount = myChores.filter((c) => (c.status === "done" || c.status === "approved") && c.completedBy === currentUser?.id).length;
  const childPendingCount = myChores.filter((c) => c.status === "pending").length;

  // Streak
  const myCompleted = currentUser ? db.chores.filter((c) =>
    c.familyCode === currentUser?.familyCode && c.completedBy === currentUser?.id && (c.status === "done" || c.status === "approved")
  ).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)) : [];

  let streak = 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 365; i++) {
    const checkDate = new Date(today); checkDate.setDate(checkDate.getDate() - i);
    const dateStr = checkDate.toISOString().split("T")[0];
    const hasChore = myCompleted.some((c) => new Date(c.completedAt).toISOString().split("T")[0] === dateStr);
    if (hasChore) streak++;
    else if (i > 0) break;
  }

  // Notifications
  const notifications = currentUser ? db.notifications.filter((n) => n.userId === currentUser.id).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) : [];
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Filtered chores for parent
  let parentChoresList = [...familyChores];
  if (choreFilter !== "all") parentChoresList = parentChoresList.filter((c) => c.status === choreFilter);
  parentChoresList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Filtered chores for child
  let childChoresList = [...myChores];
  if (childChoreFilter === "pending") childChoresList = childChoresList.filter((c) => c.status === "pending");
  else if (childChoreFilter === "done") childChoresList = childChoresList.filter((c) => c.status === "done" || c.status === "approved");
  childChoresList.sort((a, b) => { const order = { pending: 0, done: 1, approved: 2 }; return order[a.status] - order[b.status]; });

  // Activities
  const activities = currentUser ? db.activities.filter((a) => a.familyCode === currentUser.familyCode).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 15) : [];

  // Payment history
  const paymentHistory = currentUser ? db.chores.filter((c) =>
    c.familyCode === currentUser?.familyCode && c.completedBy === currentUser?.id && c.status === "approved"
  ).sort((a, b) => new Date(b.approvedAt) - new Date(a.approvedAt)) : [];

  // Family members
  const familyMembers = currentUser ? db.users.filter((u) => u.familyCode === currentUser.familyCode) : [];

  // Children for assignee dropdown
  const children = currentUser ? db.users.filter((u) => u.familyCode === currentUser.familyCode && u.role === "child") : [];

  /* ======================== RENDER ======================== */
  const toastIcons = { success: "fa-check-circle", error: "fa-exclamation-circle", info: "fa-info-circle" };

  return (
    <div className="chorevault-app">
      <HeaderWithSearch />
      <canvas ref={canvasRef} className="cv-particle-canvas" />

      {/* ========== AUTH SCREEN ========== */}
      {!currentUser && (
        <div className="cv-screen cv-active">
          <div className="cv-auth-container">
            <div className="cv-logo-section">
              <div className="cv-logo-icon"><i className="fas fa-vault"></i></div>
              <h1 className="cv-logo-text">Chore<span className="cv-accent">Vault</span></h1>
              <p className="cv-tagline">The futuristic way to earn &amp; manage allowance</p>
            </div>

            {authMode === "login" ? (
              <div className="cv-auth-form cv-glass-card">
                <h2><i className="fas fa-fingerprint"></i> Sign In</h2>
                <div className="cv-input-group">
                  <i className="fas fa-envelope cv-input-icon"></i>
                  <input type="email" placeholder="Email address" value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
                </div>
                <div className="cv-input-group">
                  <i className="fas fa-lock cv-input-icon"></i>
                  <input type="password" placeholder="Password" value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
                </div>
                <button className="cv-btn cv-btn-primary cv-btn-glow" onClick={handleLogin} style={{ width: "100%" }}>
                  <span>Access Vault</span><i className="fas fa-arrow-right"></i>
                </button>
                <p className="cv-switch-auth">Don't have an account?{" "}
                  <a href="#!" onClick={(e) => { e.preventDefault(); setAuthMode("signup"); }}>Create Account</a>
                </p>
              </div>
            ) : (
              <div className="cv-auth-form cv-glass-card">
                <h2><i className="fas fa-user-plus"></i> Create Account</h2>
                <div className="cv-input-group">
                  <i className="fas fa-user cv-input-icon"></i>
                  <input type="text" placeholder="Full name" value={signupName} onChange={(e) => setSignupName(e.target.value)} />
                </div>
                <div className="cv-input-group">
                  <i className="fas fa-envelope cv-input-icon"></i>
                  <input type="email" placeholder="Email address" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} />
                </div>
                <div className="cv-input-group">
                  <i className="fas fa-lock cv-input-icon"></i>
                  <input type="password" placeholder="Password (6+ characters)" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} />
                </div>
                <div className="cv-role-selector">
                  <label>I am a:</label>
                  <div className="cv-role-buttons">
                    <button className={`cv-role-btn ${selectedRole === "parent" ? "cv-active" : ""}`} onClick={() => setSelectedRole("parent")}>
                      <i className="fas fa-user-shield"></i> Parent
                    </button>
                    <button className={`cv-role-btn ${selectedRole === "child" ? "cv-active" : ""}`} onClick={() => setSelectedRole("child")}>
                      <i className="fas fa-child"></i> Child
                    </button>
                  </div>
                </div>
                <div className="cv-input-group">
                  <i className="fas fa-link cv-input-icon"></i>
                  <input type="text" placeholder="Family code (create or enter existing)" value={familyCode} onChange={(e) => setFamilyCode(e.target.value)} />
                </div>
                <button className="cv-btn cv-btn-primary cv-btn-glow" onClick={handleSignup} style={{ width: "100%" }}>
                  <span>Create Vault</span><i className="fas fa-rocket"></i>
                </button>
                <p className="cv-switch-auth">Already have an account?{" "}
                  <a href="#!" onClick={(e) => { e.preventDefault(); setAuthMode("login"); }}>Sign In</a>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== PARENT DASHBOARD ========== */}
      {currentUser?.role === "parent" && (
        <div className="cv-screen cv-active cv-dashboard">
          <nav className="cv-top-nav cv-glass-nav">
            <div className="cv-nav-left">
              <div className="cv-logo-small"><i className="fas fa-vault"></i> Chore<span className="cv-accent">Vault</span></div>
            </div>
            <div className="cv-nav-center">
              {[
                { key: "overview", icon: "fa-chart-line", label: "Overview" },
                { key: "chores", icon: "fa-list-check", label: "Chores" },
                { key: "analytics", icon: "fa-chart-pie", label: "Analytics" },
                { key: "family", icon: "fa-users", label: "Family" },
              ].map((tab) => (
                <button key={tab.key} className={`cv-nav-tab ${parentTab === tab.key ? "cv-active" : ""}`}
                  onClick={() => setParentTab(tab.key)}>
                  <i className={`fas ${tab.icon}`}></i><span className="cv-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>
            <div className="cv-nav-right">
              <Link to="/sales-dashboard" className="cv-eco-link" title="Track sales on VMCREATIONS">
                <i className="fas fa-chart-line"></i><span className="cv-eco-link-text"> Sales</span>
              </Link>
              <div className="cv-notification-bell" onClick={handleToggleNotifications}>
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && <span className="cv-notif-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
              </div>
              <div className="cv-user-avatar" onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); setShowNotifPanel(false); }}>
                <i className="fas fa-user-astronaut"></i>
              </div>
              {showUserMenu && (
                <div className="cv-user-menu cv-glass-card">
                  <p>{currentUser.name}</p>
                  <p className="cv-email-small">{currentUser.email}</p>
                  <p className="cv-family-code-disp">Family Code: {currentUser.familyCode}</p>
                  <hr />
                  <button onClick={handleLogout}><i className="fas fa-sign-out-alt"></i> Log Out</button>
                </div>
              )}
            </div>
          </nav>

          {/* Notifications Panel */}
          {showNotifPanel && (
            <div className="cv-notif-panel cv-glass-card">
              <h3><i className="fas fa-bell"></i> Notifications</h3>
              <div className="cv-notif-list">
                {notifications.length === 0 ? <p className="cv-empty-state">No notifications yet</p> :
                  notifications.slice(0, 20).map((n) => (
                    <div key={n.id} className={`cv-notif-item ${n.read ? "" : "cv-unread"}`}>
                      <div className="cv-notif-icon"><i className="fas fa-bell"></i></div>
                      <div className="cv-notif-text">
                        <p>{n.message}</p>
                        <span className="cv-notif-time">{timeAgo(n.timestamp)}</span>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {/* Overview Tab */}
          {parentTab === "overview" && (
            <div className="cv-tab-content">
              <div className="cv-dashboard-header">
                <div>
                  <h1>Welcome back, {currentUser.name.split(" ")[0]} 👋</h1>
                  <p className="cv-subtitle">Here's your family's chore activity at a glance</p>
                </div>
              </div>
              <div className="cv-stats-grid">
                <div className="cv-stat-card cv-glass-card cv-neon-border-cyan">
                  <div className="cv-stat-icon"><i className="fas fa-dollar-sign"></i></div>
                  <div className="cv-stat-info"><h3>${totalPaid.toFixed(2)}</h3><p>Total Paid</p></div>
                </div>
                <div className="cv-stat-card cv-glass-card cv-neon-border-pink">
                  <div className="cv-stat-icon"><i className="fas fa-calendar-check"></i></div>
                  <div className="cv-stat-info"><h3>${monthPaid.toFixed(2)}</h3><p>This Month</p></div>
                </div>
                <div className="cv-stat-card cv-glass-card cv-neon-border-green">
                  <div className="cv-stat-icon"><i className="fas fa-check-circle"></i></div>
                  <div className="cv-stat-info"><h3>{completedCount}</h3><p>Completed Chores</p></div>
                </div>
                <div className="cv-stat-card cv-glass-card cv-neon-border-purple">
                  <div className="cv-stat-icon"><i className="fas fa-clock"></i></div>
                  <div className="cv-stat-info"><h3>{pendingCount}</h3><p>Pending Chores</p></div>
                </div>
              </div>
              <div className="cv-charts-row">
                <div className="cv-chart-card cv-glass-card">
                  <h3><i className="fas fa-chart-bar"></i> Monthly Spending</h3>
                  <canvas ref={(el) => (chartCanvasRefs.current.monthlySpending = el)}></canvas>
                </div>
                <div className="cv-chart-card cv-glass-card">
                  <h3><i className="fas fa-chart-pie"></i> Chore Breakdown</h3>
                  <canvas ref={(el) => (chartCanvasRefs.current.choreBreakdown = el)}></canvas>
                </div>
              </div>
              <div className="cv-recent-activity cv-glass-card">
                <h3><i className="fas fa-stream"></i> Recent Activity</h3>
                <div className="cv-activity-list">
                  {activities.length === 0 ? <p className="cv-empty-state">No recent activity</p> :
                    activities.map((a) => {
                      const iconClass = a.type === "added" ? "cv-added" : a.type === "completed" ? "cv-completed" : a.type === "approved" ? "cv-approved" : "cv-earned";
                      const icon = a.type === "added" ? "fa-plus" : a.type === "completed" ? "fa-check" : a.type === "approved" ? "fa-dollar-sign" : "fa-star";
                      return (
                        <div key={a.id} className="cv-activity-item">
                          <div className={`cv-activity-icon ${iconClass}`}><i className={`fas ${icon}`}></i></div>
                          <div className="cv-activity-details">
                            <p>{a.message}</p>
                            <span className="cv-activity-time">{timeAgo(a.timestamp)}</span>
                          </div>
                          {a.amount ? <span className="cv-activity-amount">${a.amount.toFixed(2)}</span> : null}
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          )}

          {/* Chores Tab */}
          {parentTab === "chores" && (
            <div className="cv-tab-content">
              <div className="cv-dashboard-header">
                <h1><i className="fas fa-list-check"></i> Manage Chores</h1>
                <button className="cv-btn cv-btn-primary cv-btn-glow" onClick={() => setShowAddChoreModal(true)}>
                  <i className="fas fa-plus"></i> Add New Chore
                </button>
              </div>
              <div className="cv-chore-filters">
                {["all", "pending", "done", "approved"].map((f) => (
                  <button key={f} className={`cv-filter-btn ${choreFilter === f ? "cv-active" : ""}`}
                    onClick={() => setChoreFilter(f)}>
                    {f === "all" ? "All" : f === "pending" ? "Pending" : f === "done" ? "Completed" : "Approved"}
                  </button>
                ))}
              </div>
              <div className="cv-chore-grid">
                {parentChoresList.length === 0 ? (
                  <p className="cv-empty-state">No chores found. Click "Add New Chore" to get started!</p>
                ) : parentChoresList.map((chore) => {
                  const assignee = chore.assigneeId === "all" ? "All Children" : (db.users.find((u) => u.id === chore.assigneeId)?.name || "Unknown");
                  const statusClass = chore.status === "pending" ? "cv-status-pending" : chore.status === "done" ? "cv-status-done" : "cv-status-approved";
                  const statusLabel = chore.status === "pending" ? "⏳ Pending" : chore.status === "done" ? "✅ Done" : "💰 Approved";
                  const completedByUser = db.users.find((u) => u.id === chore.completedBy);

                  return (
                    <div key={chore.id} className={`cv-chore-card ${chore.status === "done" ? "cv-done" : chore.status === "approved" ? "cv-approved" : ""}`}>
                      <div className="cv-chore-card-header">
                        <h3>{getCategoryEmoji(chore.category)} {chore.name}</h3>
                        <span className="cv-chore-amount">${chore.amount.toFixed(2)}</span>
                      </div>
                      {chore.description && <p className="cv-chore-card-desc">{chore.description}</p>}
                      <div className="cv-chore-meta">
                        <span className="cv-chore-tag cv-category">{chore.category}</span>
                        <span className="cv-chore-tag cv-recurrence">{chore.recurrence}</span>
                        <span className={`cv-chore-tag ${statusClass}`}>{statusLabel}</span>
                        <span className="cv-chore-tag">👤 {assignee}</span>
                      </div>
                      {chore.status === "done" && (
                        <>
                          <div className="cv-chore-actions">
                            <button className="cv-btn cv-btn-success" onClick={() => approveChore(chore.id)}>
                              <i className="fas fa-check"></i> Approve &amp; Pay
                            </button>
                            <button className="cv-btn cv-btn-danger" onClick={() => rejectChore(chore.id)}>
                              <i className="fas fa-undo"></i> Reject
                            </button>
                          </div>
                          <p style={{ fontSize: "0.8rem", color: "var(--cv-text-muted)", marginTop: 8 }}>
                            Completed by: {completedByUser?.name || "Unknown"}
                          </p>
                        </>
                      )}
                      {chore.status === "pending" && (
                        <div className="cv-chore-actions">
                          <button className="cv-btn cv-btn-danger" onClick={() => deleteChore(chore.id)}>
                            <i className="fas fa-trash"></i> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {parentTab === "analytics" && (
            <div className="cv-tab-content">
              <div className="cv-dashboard-header">
                <div>
                  <h1><i className="fas fa-chart-pie"></i> Spending Analytics</h1>
                  <p className="cv-subtitle">Deep dive into your chore spending patterns</p>
                </div>
              </div>
              <div className="cv-analytics-grid">
                <div className="cv-chart-card cv-glass-card cv-full-width">
                  <h3><i className="fas fa-chart-area"></i> Spending Over Time</h3>
                  <canvas ref={(el) => (chartCanvasRefs.current.spendingOverTime = el)}></canvas>
                </div>
                <div className="cv-chart-card cv-glass-card">
                  <h3><i className="fas fa-child"></i> Per-Child Spending</h3>
                  <canvas ref={(el) => (chartCanvasRefs.current.perChild = el)}></canvas>
                </div>
                <div className="cv-chart-card cv-glass-card">
                  <h3><i className="fas fa-tags"></i> Top Chore Categories</h3>
                  <canvas ref={(el) => (chartCanvasRefs.current.topCategories = el)}></canvas>
                </div>
              </div>
            </div>
          )}

          {/* Family Tab */}
          {parentTab === "family" && (
            <div className="cv-tab-content">
              <div className="cv-dashboard-header">
                <div>
                  <h1><i className="fas fa-users"></i> Family Members</h1>
                  <p className="cv-subtitle">Manage your family and share your family code</p>
                </div>
              </div>
              <div className="cv-family-code-card cv-glass-card cv-neon-border-cyan">
                <h2>Your Family Code</h2>
                <div className="cv-code-display">
                  <span>{currentUser.familyCode}</span>
                  <button className="cv-btn-small" onClick={copyFamilyCode}><i className="fas fa-copy"></i> Copy</button>
                </div>
                <p>Share this code with your children so they can join your family vault</p>
              </div>
              <div className="cv-family-members-grid">
                {familyMembers.length === 0 ? <p className="cv-empty-state">No family members yet</p> :
                  familyMembers.map((m) => {
                    const isParent = m.role === "parent";
                    const memberApproved = db.chores.filter((c) => c.familyCode === m.familyCode && c.completedBy === m.id && c.status === "approved");
                    const memberEarned = memberApproved.reduce((sum, c) => sum + c.amount, 0);
                    return (
                      <div key={m.id} className="cv-member-card">
                        <div className={`cv-member-avatar ${isParent ? "cv-parent-avatar" : "cv-child-avatar"}`}>
                          <i className={`fas ${isParent ? "fa-user-shield" : "fa-child"}`}></i>
                        </div>
                        <h4>{m.name}</h4>
                        <p className="cv-member-role">{m.role}</p>
                        {!isParent && <p className="cv-member-stat">${memberEarned.toFixed(2)} earned</p>}
                      </div>
                    );
                  })
                }
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== CHILD DASHBOARD ========== */}
      {currentUser?.role === "child" && (
        <div className="cv-screen cv-active cv-dashboard">
          <nav className="cv-top-nav cv-glass-nav">
            <div className="cv-nav-left">
              <div className="cv-logo-small"><i className="fas fa-vault"></i> Chore<span className="cv-accent">Vault</span></div>
            </div>
            <div className="cv-nav-center">
              {[
                { key: "childOverview", icon: "fa-home", label: "Home" },
                { key: "childChores", icon: "fa-tasks", label: "My Chores" },
                { key: "childEarnings", icon: "fa-coins", label: "Earnings" },
              ].map((tab) => (
                <button key={tab.key} className={`cv-nav-tab ${childTab === tab.key ? "cv-active" : ""}`}
                  onClick={() => setChildTab(tab.key)}>
                  <i className={`fas ${tab.icon}`}></i><span className="cv-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>
            <div className="cv-nav-right">
              <Link to="/sales-dashboard" className="cv-eco-link" title="Track sales on VMCREATIONS">
                <i className="fas fa-chart-line"></i><span className="cv-eco-link-text"> Sales</span>
              </Link>
              <div className="cv-notification-bell" onClick={handleToggleNotifications}>
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && <span className="cv-notif-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
              </div>
              <div className="cv-user-avatar" onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); setShowNotifPanel(false); }}>
                <i className="fas fa-user-astronaut"></i>
              </div>
              {showUserMenu && (
                <div className="cv-user-menu cv-glass-card">
                  <p>{currentUser.name}</p>
                  <p className="cv-email-small">{currentUser.email}</p>
                  <hr />
                  <button onClick={handleLogout}><i className="fas fa-sign-out-alt"></i> Log Out</button>
                </div>
              )}
            </div>
          </nav>

          {/* Notifications Panel */}
          {showNotifPanel && (
            <div className="cv-notif-panel cv-glass-card">
              <h3><i className="fas fa-bell"></i> Notifications</h3>
              <div className="cv-notif-list">
                {notifications.length === 0 ? <p className="cv-empty-state">No notifications yet</p> :
                  notifications.slice(0, 20).map((n) => (
                    <div key={n.id} className={`cv-notif-item ${n.read ? "" : "cv-unread"}`}>
                      <div className="cv-notif-icon"><i className="fas fa-bell"></i></div>
                      <div className="cv-notif-text">
                        <p>{n.message}</p>
                        <span className="cv-notif-time">{timeAgo(n.timestamp)}</span>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {/* Child Overview Tab */}
          {childTab === "childOverview" && (
            <div className="cv-tab-content">
              <div className="cv-dashboard-header">
                <div>
                  <h1>Hey {currentUser.name.split(" ")[0]}! 🚀</h1>
                  <p className="cv-subtitle">Ready to earn some cash today?</p>
                </div>
              </div>
              <div className="cv-stats-grid">
                <div className="cv-stat-card cv-glass-card cv-neon-border-cyan">
                  <div className="cv-stat-icon"><i className="fas fa-piggy-bank"></i></div>
                  <div className="cv-stat-info"><h3>${childTotalEarned.toFixed(2)}</h3><p>Total Earned</p></div>
                </div>
                <div className="cv-stat-card cv-glass-card cv-neon-border-pink">
                  <div className="cv-stat-icon"><i className="fas fa-fire"></i></div>
                  <div className="cv-stat-info"><h3>${childMonthEarned.toFixed(2)}</h3><p>This Month</p></div>
                </div>
                <div className="cv-stat-card cv-glass-card cv-neon-border-green">
                  <div className="cv-stat-icon"><i className="fas fa-trophy"></i></div>
                  <div className="cv-stat-info"><h3>{childCompletedCount}</h3><p>Chores Done</p></div>
                </div>
                <div className="cv-stat-card cv-glass-card cv-neon-border-purple">
                  <div className="cv-stat-icon"><i className="fas fa-bolt"></i></div>
                  <div className="cv-stat-info"><h3>{childPendingCount}</h3><p>Available Chores</p></div>
                </div>
              </div>
              <div className="cv-charts-row">
                <div className="cv-chart-card cv-glass-card">
                  <h3><i className="fas fa-chart-bar"></i> My Monthly Earnings</h3>
                  <canvas ref={(el) => (chartCanvasRefs.current.childMonthly = el)}></canvas>
                </div>
                <div className="cv-chart-card cv-glass-card">
                  <h3><i className="fas fa-star"></i> Chore Streak</h3>
                  <div className="cv-streak-display">
                    <div className="cv-streak-number">{streak}</div>
                    <p>Day Streak 🔥</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Child Chores Tab */}
          {childTab === "childChores" && (
            <div className="cv-tab-content">
              <div className="cv-dashboard-header">
                <div>
                  <h1><i className="fas fa-tasks"></i> My Chores</h1>
                  <p className="cv-subtitle">Complete chores to earn cash!</p>
                </div>
              </div>
              <div className="cv-chore-filters">
                {["all", "pending", "done"].map((f) => (
                  <button key={f} className={`cv-filter-btn ${childChoreFilter === f ? "cv-active" : ""}`}
                    onClick={() => setChildChoreFilter(f)}>
                    {f === "all" ? "All" : f === "pending" ? "To Do" : "Done"}
                  </button>
                ))}
              </div>
              <div className="cv-chore-grid">
                {childChoresList.length === 0 ? (
                  <p className="cv-empty-state">No chores available right now. Check back soon! 🎮</p>
                ) : childChoresList.map((chore) => {
                  const statusClass = chore.status === "pending" ? "cv-status-pending" : chore.status === "done" ? "cv-status-done" : "cv-status-approved";
                  const statusLabel = chore.status === "pending" ? "⏳ To Do" : chore.status === "done" ? "⏳ Awaiting Approval" : "💰 Paid!";
                  return (
                    <div key={chore.id} className={`cv-chore-card ${chore.status === "done" ? "cv-done" : chore.status === "approved" ? "cv-approved" : ""}`}>
                      <div className="cv-chore-card-header">
                        <h3>{getCategoryEmoji(chore.category)} {chore.name}</h3>
                        <span className="cv-chore-amount">${chore.amount.toFixed(2)}</span>
                      </div>
                      {chore.description && <p className="cv-chore-card-desc">{chore.description}</p>}
                      <div className="cv-chore-meta">
                        <span className="cv-chore-tag cv-category">{chore.category}</span>
                        <span className={`cv-chore-tag ${statusClass}`}>{statusLabel}</span>
                      </div>
                      {chore.status === "pending" && (
                        <div className="cv-chore-actions">
                          <button className="cv-btn cv-btn-success" onClick={() => markChoreDone(chore.id)}>
                            <i className="fas fa-check-circle"></i> Mark Done!
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Child Earnings Tab */}
          {childTab === "childEarnings" && (
            <div className="cv-tab-content">
              <div className="cv-dashboard-header">
                <div>
                  <h1><i className="fas fa-coins"></i> My Earnings</h1>
                  <p className="cv-subtitle">Track all the cash you've earned</p>
                </div>
              </div>
              <div className="cv-analytics-grid">
                <div className="cv-chart-card cv-glass-card cv-full-width">
                  <h3><i className="fas fa-chart-area"></i> Earnings Over Time</h3>
                  <canvas ref={(el) => (chartCanvasRefs.current.childEarnings = el)}></canvas>
                </div>
                <div className="cv-earnings-history cv-glass-card cv-full-width">
                  <h3><i className="fas fa-receipt"></i> Payment History</h3>
                  <div className="cv-activity-list">
                    {paymentHistory.length === 0 ? <p className="cv-empty-state">No payments yet. Complete chores to earn cash! 💪</p> :
                      paymentHistory.map((c) => (
                        <div key={c.id} className="cv-activity-item">
                          <div className="cv-activity-icon cv-earned"><i className="fas fa-dollar-sign"></i></div>
                          <div className="cv-activity-details">
                            <p>{getCategoryEmoji(c.category)} {c.name}</p>
                            <span className="cv-activity-time">{formatDate(c.approvedAt)}</span>
                          </div>
                          <span className="cv-activity-amount">${c.amount.toFixed(2)}</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== ADD CHORE MODAL ========== */}
      {showAddChoreModal && (
        <div className="cv-modal-overlay" onClick={() => setShowAddChoreModal(false)}>
          <div className="cv-modal cv-glass-card" onClick={(e) => e.stopPropagation()}>
            <div className="cv-modal-header">
              <h2><i className="fas fa-plus-circle"></i> Add New Chore</h2>
              <button className="cv-modal-close" onClick={() => setShowAddChoreModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="cv-modal-body">
              <div className="cv-input-group">
                <i className="fas fa-broom cv-input-icon"></i>
                <input type="text" placeholder='Chore name (e.g., Clean Room)' value={choreName} onChange={(e) => setChoreName(e.target.value)} />
              </div>
              <div className="cv-input-group">
                <i className="fas fa-align-left cv-input-icon"></i>
                <textarea placeholder="Description (optional)" rows="2" value={choreDesc} onChange={(e) => setChoreDesc(e.target.value)}></textarea>
              </div>
              <div className="cv-input-row">
                <div className="cv-input-group cv-half">
                  <i className="fas fa-dollar-sign cv-input-icon"></i>
                  <input type="number" placeholder="Amount ($)" min="0" step="0.25" value={choreAmount} onChange={(e) => setChoreAmount(e.target.value)} />
                </div>
                <div className="cv-input-group cv-half">
                  <i className="fas fa-tag cv-input-icon"></i>
                  <select value={choreCategory} onChange={(e) => setChoreCategory(e.target.value)}>
                    <option value="cleaning">🧹 Cleaning</option>
                    <option value="kitchen">🍽️ Kitchen</option>
                    <option value="outdoor">🌿 Outdoor</option>
                    <option value="laundry">👕 Laundry</option>
                    <option value="pets">🐾 Pets</option>
                    <option value="homework">📚 Homework</option>
                    <option value="other">⭐ Other</option>
                  </select>
                </div>
              </div>
              <div className="cv-input-group">
                <i className="fas fa-child cv-input-icon"></i>
                <select value={choreAssignee} onChange={(e) => setChoreAssignee(e.target.value)}>
                  <option value="all">Assign to all children</option>
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>{child.name}</option>
                  ))}
                </select>
              </div>
              <div className="cv-input-group">
                <i className="fas fa-redo cv-input-icon"></i>
                <select value={choreRecurrence} onChange={(e) => setChoreRecurrence(e.target.value)}>
                  <option value="once">One time</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
            </div>
            <div className="cv-modal-footer">
              <button className="cv-btn cv-btn-secondary" onClick={() => setShowAddChoreModal(false)}>Cancel</button>
              <button className="cv-btn cv-btn-primary cv-btn-glow" onClick={addChore}>
                <i className="fas fa-plus"></i> Add Chore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== TOAST NOTIFICATIONS ========== */}
      <div className="cv-toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`cv-toast cv-toast-${t.type} ${t.exiting ? "cv-toast-exit" : ""}`}>
            <i className={`fas ${toastIcons[t.type] || toastIcons.info}`}></i>
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
